import type { BuildFlightCampaignBody } from '~/services/FlightsService';

import type { CorridorOpportunity } from '~/types/corridorOpportunity';

import type { OnSaveFormData } from '../FlightsCampaigns/FlightsCampaignsForm';
import { initialState } from '../FlightsCampaigns/FlightsCampaignsForm';
import { parseCampaignLuxPlusPrice } from '../FlightsCampaigns/campaignPricing';

// The engine owns the copy, route, travel dates and fares. Admin completes the review requirements.
export function campaignFromOpportunity(opportunity: CorridorOpportunity): OnSaveFormData {
  const { formId: _formId, formErrors: _formErrors, ...defaults } = initialState;
  return { ...defaults, ...opportunity.campaignDraft.campaign };
}

export function getOpportunityBuildError(opportunity: CorridorOpportunity, now = Date.now()): string | undefined {
  if (opportunity.campaignId) return 'This opportunity already has a campaign. Open it from Opportunities.';
  if (!['APPROVED', 'DRAFT_READY'].includes(opportunity.status) || !opportunity.campaignDraft) {
    return 'Create or refresh the private draft from Opportunities before reviewing it.';
  }
  const { campaignDraft: draft, priceValidation: validation } = opportunity;
  if (
    !validation ||
    draft.sourceValidationId !== validation.id ||
    !(new Date(validation.expiresAt).getTime() > now) ||
    !(new Date(draft.expiresAt).getTime() > now) ||
    !opportunity.qualification?.qualified
  ) {
    return 'Refresh the fare and private draft from Opportunities before saving. The evidence is stale or no longer qualifies.';
  }
}

export function toOpportunityBuildBody(
  opportunity: CorridorOpportunity,
  campaign: OnSaveFormData,
  bookingClassCode: string,
): BuildFlightCampaignBody {
  const deal = opportunity.campaignDraft.flightDeal;
  return {
    opportunityId: opportunity.id,
    campaign: {
      type: campaign.type as BuildFlightCampaignBody['campaign']['type'],
      title: campaign.title,
      region: campaign.region,
      header: campaign.header,
      description: campaign.description,
      start_date: campaign.startDate,
      end_date: campaign.endDate,
      brand: campaign.brand.join(','),
      hero_image_public_id: campaign.heroImage,
      terms: campaign.terms,
      airline_code: campaign.airlineCode,
      price: Number(campaign.price),
      lux_plus_price: parseCampaignLuxPlusPrice(campaign.luxPlusPrice),
      fare_type: campaign.fareType,
      fare_class: campaign.fareClass,
    },
    dealTemplate: {
      carrierCode: deal.carrierCode,
      fareType: deal.fareType,
      offerDurations: [{ departureDate: deal.departureDate1, arrivalDate: deal.arrivalDate1 }],
      categoryGroup: deal.categoryGroup,
      adultCount: deal.adultCount,
      discountLabel: deal.discountLabel,
      baggageIncluded: false,
    },
    rows: [
      {
        carrierCode: deal.carrierCode,
        originAirportCode: deal.originAirportCode,
        destinationAirportCode: deal.destinationAirportCode,
        fareClass: deal.fareClass.toLowerCase() as 'economy',
        perAdultPrice: Number(deal.perAdultPrice),
        luxPlusPerAdultPrice: deal.luxPlusPerAdultPrice ? Number(deal.luxPlusPerAdultPrice) : null,
        bookingClassCode: bookingClassCode.trim().toUpperCase(),
        dealName: deal.dealName,
        dealDescription: deal.dealDescription,
      },
    ],
    publish: 'draft',
  };
}
